import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Gamepad2, Briefcase, Video, Bitcoin, GraduationCap, Dumbbell } from "lucide-react";

const ExamplesPreview = () => {
  const categories = [
    { icon: Gamepad2, name: "Gaming", color: "from-red-500 to-orange-500" },
    { icon: Briefcase, name: "Business", color: "from-blue-500 to-cyan-500" },
    { icon: Video, name: "Vlogging", color: "from-pink-500 to-purple-500" },
    { icon: Bitcoin, name: "Crypto", color: "from-yellow-500 to-orange-500" },
    { icon: GraduationCap, name: "Education", color: "from-green-500 to-teal-500" },
    { icon: Dumbbell, name: "Fitness", color: "from-purple-500 to-pink-500" },
  ];

  const examples = [
    { niche: "Gaming", title: "How I Reached Immortal in 30 Days", views: "2.4M views" },
    { niche: "Business", title: "My $1M Side Hustle Strategy", views: "890K views" },
    { niche: "Vlogging", title: "Day in My Life in Tokyo", views: "1.2M views" },
    { niche: "Crypto", title: "This Altcoin Will 100x", views: "567K views" },
    { niche: "Education", title: "Learn Python in 24 Hours", views: "3.1M views" },
    { niche: "Fitness", title: "30 Day Body Transformation", views: "1.8M views" },
  ];

  return (
    <section className="section-padding relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-card/50 to-background" />

      <div className="container-custom relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-sm font-semibold text-brand-pink uppercase tracking-wider mb-4 block">
            Examples Gallery
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            Thumbnails That{" "}
            <span className="gradient-text">Actually Convert</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            See real examples across different niches. Every thumbnail is AI-generated and optimized for clicks.
          </p>
        </div>

        {/* Categories */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((cat, index) => (
            <button
              key={index}
              className={`flex items-center gap-2 px-4 py-2 rounded-full border border-border bg-card/50 hover:border-primary/50 transition-all ${
                index === 0 ? "border-primary bg-primary/10" : ""
              }`}
            >
              <cat.icon className="w-4 h-4" />
              <span className="text-sm font-medium">{cat.name}</span>
            </button>
          ))}
        </div>

        {/* Examples Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {examples.map((example, index) => (
            <div
              key={index}
              className="group relative rounded-2xl overflow-hidden border border-border bg-card hover:border-primary/30 transition-all duration-300"
            >
              {/* Thumbnail Placeholder */}
              <div className="aspect-video bg-gradient-to-br from-secondary to-muted relative overflow-hidden">
                <div className={`absolute inset-0 bg-gradient-to-br ${categories[index].color} opacity-30`} />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-2xl bg-background/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                      {(() => {
                        const IconComponent = categories[index].icon;
                        return <IconComponent className="w-8 h-8 text-foreground" />;
                      })()}
                    </div>
                    <span className="text-sm font-medium text-foreground/80">{example.niche}</span>
                  </div>
                </div>

                {/* Before/After Badge */}
                <div className="absolute top-3 left-3 px-2 py-1 rounded-lg bg-background/80 backdrop-blur-sm text-xs font-medium">
                  AI Generated
                </div>
              </div>

              {/* Info */}
              <div className="p-4">
                <h4 className="font-semibold mb-1 line-clamp-1">{example.title}</h4>
                <p className="text-sm text-muted-foreground">{example.views}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link to="/examples">
            <Button variant="gradient" size="lg" className="group">
              View All Examples
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ExamplesPreview;
